import { create } from "zustand";
import { persist } from "zustand/middleware";
import { v4 as uuidv4 } from "uuid";
import { STRATEGY_STORE_KEY } from "./stores";
import { FUNDS } from "@/db/funds";
import { findNextName } from "@/utils/find-next-name";
import i18n from "@/lib/i18n";

export const STRATEGY_TYPES = ["age-based", "goal-based"] as const;
export type StrategyType = (typeof STRATEGY_TYPES)[number];

/**
 * A life event that withdraws funds from the investment
 */
export type LifeEvent = {
  id: string;
  name: string;
  age: number;
  amount: number;
};

type StrategyBase = {
  id: string;
  name: string;
  createdAt: Date;
  type: StrategyType;
  currentAge: number;
  initialAmount: number;
  monthlyContribution: number;
  selectedFund: (typeof FUNDS)[number]["id"];
  inflationRate: number;
  taxRate: number;
  lifeEvents?: LifeEvent[];
};

export type AgeBasedStrategy = StrategyBase & {
  type: (typeof STRATEGY_TYPES)[0];
  goalAge: number;
};

export type GoalBasedStrategy = StrategyBase & {
  type: (typeof STRATEGY_TYPES)[1];
  goal: number;
  goalAge: number;
};

export type Strategy = AgeBasedStrategy | GoalBasedStrategy;

/**
 * Default values for a new age-based strategy
 */
const DEFAULT_STRATEGY_VALUES = {
  type: "age-based" as const,
  currentAge: 25,
  inflationRate: 3,
  initialAmount: 10000,
  monthlyContribution: 500,
  selectedFund: FUNDS[0].id,
  goalAge: 65,
  taxRate: 13,
  lifeEvents: [] as LifeEvent[],
};

/**
 * Creates a new strategy with default values
 */
function createStrategy(name: string): AgeBasedStrategy {
  return {
    id: uuidv4(),
    name,
    createdAt: new Date(),
    ...DEFAULT_STRATEGY_VALUES,
  };
}

type StrategyStore = {
  strategies: Strategy[];
  addStrategy: (strategy: Strategy) => void;
  removeStrategy: (id: string) => void;
  newBlankStrategy: () => void;
  updateStrategy: (strategy: Strategy) => void;
  reorderStrategies: (strategies: Strategy[]) => void;
  resetStrategies: () => void;
};

export const useStrategyStore = create<StrategyStore>()(
  persist(
    (set) => ({
      strategies: [createStrategy(i18n.t("home.defaultStrategyName"))],
      newBlankStrategy: () =>
        set((state) => ({
          strategies: [
            ...state.strategies,
            createStrategy(
              findNextName(
                state.strategies.map((s) => s.name),
                i18n.t("home.newStrategy")
              )
            ),
          ],
        })),
      addStrategy: (strategy: Strategy) =>
        set((state) => ({ strategies: [...state.strategies, strategy] })),
      removeStrategy: (id: string) =>
        set((state) => ({
          strategies: state.strategies.filter((strategy) => strategy.id !== id),
        })),
      updateStrategy: (strategy: Strategy) =>
        set((state) => ({
          strategies: state.strategies.map((s) =>
            s.id === strategy.id ? strategy : s
          ),
        })),
      reorderStrategies: (strategies: Strategy[]) => set({ strategies }),
      resetStrategies: () => set({ strategies: [] }),
    }),
    { name: STRATEGY_STORE_KEY }
  )
);
